import { faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { completeIntakeFormCardOnFileThunk } from 'actions/intakeForm/IntakeFormActions';
import { createPaymentMethodSessionThunk } from 'actions/paymentMethods/PaymentMethodsActions';
import { AdyenAddCardDropIn } from 'components/AdyenDropIn/AdyenAddCardDropIn';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { Alert } from 'components/UI/Alert/Alert';
import { Button } from 'components/UI/Button/Button';
import { Spinner } from 'components/UI/Spinner/Spinner';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getIntakeFormCustomer } from 'reducers/intakeForm/IntakeFormSelectors';
import {
    getAdyenSession,
    getSessionLoading
} from 'reducers/paymentMethods/PaymentMethodsSelectors';

export const IntakeFormCardOnFile: React.FC = () => {
    const dispatch = useDispatch();
    const customer = useSelector(getIntakeFormCustomer);
    const adyenSession = useSelector(getAdyenSession);
    const sessionLoading = useSelector(getSessionLoading);

    const [hasError, setHasError] = React.useState(false);
    const [hasRequestedSession, setHasRequestedSession] = React.useState(false);
    const [hasSavedCard, setHasSavedCard] = React.useState(false);

    const createSession = React.useCallback(() => {
        if (!customer.id) return;

        setHasError(false);
        setHasSavedCard(false);
        setHasRequestedSession(true);
        dispatch(createPaymentMethodSessionThunk(customer.id));
    }, [customer.id, dispatch]);

    React.useEffect(() => {
        if (customer.id) {
            createSession();
        }
        // Bootstrap Adyen session once when entering the step.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customer.id]);

    const handleCardAdded = (success: boolean) => {
        if (success) {
            setHasError(false);
            setHasSavedCard(true);
            return;
        }

        setHasSavedCard(false);
        setHasError(true);
    };

    const handleCardAddError = () => {
        setHasSavedCard(false);
        setHasError(true);
    };

    const handleSubmit = () => {
        if (!hasSavedCard) return;

        dispatch(completeIntakeFormCardOnFileThunk());
    };

    const showSpinner = sessionLoading || !hasRequestedSession;
    const showDropIn = !!adyenSession && !sessionLoading;
    const showRetry = hasRequestedSession && !sessionLoading && (!adyenSession || hasError);

    return (
        <LeftColumn
            contentClassName="flex flex-col flex-1"
            footer={
                <Button fullWidth disabled={!hasSavedCard} onClick={handleSubmit}>
                    Submit
                </Button>
            }
        >
            <h1 className="h1 underlined w-full">Card On File</h1>

            <p className="text-base w-full mt-6 mb-6">
                A valid credit or debit card is required to complete your registration. Your card
                will not be charged at this time.
            </p>

            {(hasError || showRetry) && (
                <Alert variant="error" icon={faTimesCircle} className="mb-5 w-full">
                    An error has occurred while saving your card. Please try again.
                </Alert>
            )}

            {showSpinner && (
                <div className="w-full text-center mt-6">
                    <Spinner size={50} />
                </div>
            )}

            {showDropIn && adyenSession && (
                <div className="w-full">
                    <AdyenAddCardDropIn
                        session={adyenSession}
                        payButtonLabel="Save Details"
                        onComplete={handleCardAdded}
                        onError={handleCardAddError}
                    />
                </div>
            )}

            {showRetry && (
                <div className="w-full mt-4">
                    <Button onClick={createSession}>Retry</Button>
                </div>
            )}
        </LeftColumn>
    );
};
